import SectionWrapper from '../SectionWrapper';
import {
  FormControl,
  Input,
  FormLabel,
  Flex,
  Tooltip,
  IconButton,
  useToast,
  Box,
  Text,
} from '@chakra-ui/react';
import { useAppSelector } from '../../../../store/redux/hooks';
import FieldEditIndicator from '../FieldEditIndicator';
import { useRef } from 'react';
import { useFieldEditIndicator } from '../hooks/useFieldEditIndicator';
import CustomCopyIcon from '../../../../theme/icons/CustomCopyIcon';
import CustomVisitUrlIcon from '../../../../theme/icons/CustomVisitUrlIcon';
import CustomToast from '../../../../common/CustomToast';
import { RootState } from '../../../../store/redux/store';
import { useTemplateFieldValidation } from '../validation/useTemplateFieldValidation';
import FormQrCode from './FormQrCode';

const DistributionLink = () => {
  const formEnabled = useAppSelector((s) => s.form.enabled);
  const slug = useAppSelector((s) => s.form.slug);
  const urlRef = useRef<HTMLDivElement>(null);
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { handleFocus } = useTemplateFieldValidation('form');
  const { activeFieldRef, isFieldBeingEdited } = useFieldEditIndicator({
    fieldRefMap: {
      url: urlRef,
      qrCode: qrCodeRef,
    },
  });
  const queryParams = useAppSelector(
    (state: RootState) => state.form.queryParams
  );
  const queryParamsString =
    queryParams.length > 0
      ? `?${queryParams.map((param) => `${param.id}=yourValue`).join('&')}`
      : '';
  const formUrl = `${process.env.REACT_APP_FORMS_BASE_URL || window.env.REACT_APP_FORMS_BASE_URL}/${slug}${queryParamsString}`;

  return (
    <SectionWrapper
      title="Distribution link"
      openOnLoad={true}
      id="distribution-link-section"
      tooltip="Each customer must complete the form to receive a pass. The link or QR code can be reused."
      subtitle="Share this form by copying the link or downloading a QR code."
    >
      <Box
        opacity={formEnabled ? 1 : 0.5}
        pointerEvents={formEnabled ? 'auto' : 'none'}
        position="relative"
      >
        {!formEnabled && (
          <Text fontSize="14px" color="grey" mb={4}>
            Enable the pass issuance form above to share your link and QR code.
          </Text>
        )}
        <Flex direction="column" gap={6}>
          <FieldEditIndicator
            fieldRef={urlRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="62px"
          >
            <FormControl>
              <Flex
                direction="column"
                gap={2}
                onFocus={() => handleFocus('url')}
              >
                <FormLabel variant="templateBuilderLabel">URL</FormLabel>
                <Flex direction="row" gap={2} alignItems="center">
                  <Input
                    variant="readOnlyUrl"
                    bg="#F8F8F8"
                    id="formUrl"
                    type="text"
                    value={formUrl}
                    isReadOnly
                  />
                  <Tooltip
                    label="Go to URL"
                    hasArrow
                    placement="top"
                    fontSize="12px"
                    bg="inkBlue"
                    color="white"
                    borderRadius="6px"
                    textAlign="center"
                    p="10px"
                  >
                    <IconButton
                      icon={<CustomVisitUrlIcon boxSize="20px" />}
                      onClick={() => {
                        window.open(formUrl, '_blank');
                      }}
                      aria-label="Go to URL"
                      variant="ghost"
                      borderRadius="10px"
                      borderColor="#D0D5DD"
                      borderWidth="1px"
                      bg="#F8F8F8"
                      _hover={{
                        bg: '#EEEEEE',
                        '& path': { stroke: '#252525' },
                      }}
                    />
                  </Tooltip>
                  <Tooltip
                    label="Copy URL"
                    hasArrow
                    placement="top"
                    fontSize="12px"
                    bg="inkBlue"
                    color="white"
                    borderRadius="6px"
                    textAlign="center"
                    p="10px"
                  >
                    <IconButton
                      icon={<CustomCopyIcon boxSize="20px" color="#989898" />}
                      onClick={() => {
                        navigator.clipboard.writeText(formUrl);
                        toast({
                          render: (props) => (
                            <CustomToast
                              status="success"
                              title="Link copied"
                              description=""
                              onClose={props.onClose}
                              position="top"
                              duration={2000}
                              id={props.id}
                            />
                          ),
                        });
                      }}
                      cursor="pointer"
                      aria-label="Copy URL"
                      variant="ghost"
                      borderRadius="10px"
                      borderColor="#D0D5DD"
                      borderWidth="1px"
                      bg="#F8F8F8"
                      _hover={{
                        bg: '#EEEEEE',
                        '& path': { stroke: '#252525' },
                      }}
                    />
                  </Tooltip>
                </Flex>
              </Flex>
            </FormControl>
          </FieldEditIndicator>
          <FieldEditIndicator
            fieldRef={qrCodeRef}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-40px"
            top="195px"
          >
            <FormControl>
              <Flex
                direction="column"
                gap={2}
                onFocus={() => handleFocus('qrCode')}
              >
                <FormLabel variant="templateBuilderLabel">QR code</FormLabel>
                <Box
                  w="100%"
                  border="1px solid #D0D5DD"
                  borderRadius="10px"
                  bg="#F8F8F8"
                  py={6}
                  px={10}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Flex direction="column" gap={6} alignItems="center" w="100%">
                    <FormQrCode value={formUrl} />
                  </Flex>
                </Box>
              </Flex>
            </FormControl>
          </FieldEditIndicator>
        </Flex>
      </Box>
    </SectionWrapper>
  );
};

export default DistributionLink;
